import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { BaseResponseCount } from '../../response_counts.js';
import { SocialProofAvatars } from '@/components/molecules/SocialProofAvatars.jsx';
import { Confetti } from '../../Confetti.jsx';
import { PorangeButton } from "@/components/atoms/PorangeButton.jsx";

const formatCount = (count) => {
    const inThousands = count / 1000;
    return inThousands % 1 === 0 
        ? `${inThousands.toFixed(0)}k`
        : `${inThousands.toFixed(1)}k`;
};

export const Question = ({ 
    data, 
    onAnswer, 
    flowVersion,
    previousAnswer,
    isTransitioning,
    direction,
    onTransitionComplete,
    skipAnswerAnimation = false,
    setClickPosition,
    setShowConfetti,
    isInSlider = false,
    forceAnswerAnimation = false
}) => {
    const [selectedAnswers, setSelectedAnswers] = useState(() => {
        if (!previousAnswer) return [];
        return Array.isArray(previousAnswer) ? previousAnswer : [previousAnswer];
    });
    const [showResults, setShowResults] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [showContent, setShowContent] = useState(false);
    const [showConfetti, setLocalShowConfetti] = useState(false);
    const [clickPosition, setLocalClickPosition] = useState({ x: 0, y: 0 });
    const [activeEmojis, setActiveEmojis] = useState([]);
    
    useEffect(() => {
        setShowResults(false);
        setSelectedAnswers(() => {
            if (!previousAnswer) return [];
            return Array.isArray(previousAnswer) ? previousAnswer : [previousAnswer];
        });
        setIsAnimating(false);
        
        if (!isTransitioning) {
            setTimeout(() => setShowContent(true), 150);
        } else {
            setShowContent(false);
        }
    }, [data, isTransitioning, previousAnswer]);

    useEffect(() => {
        if (forceAnswerAnimation && previousAnswer && !showResults) {
            setTimeout(() => {
                setShowResults(true);
            }, 500);
        }
    }, [forceAnswerAnimation, previousAnswer]);

    const handleOptionClick = (option, event) => {
        if (isTransitioning || isAnimating) return;
        
        if (data.isMultipleChoice) {
            setSelectedAnswers(prev => {
                const isSelected = prev.some(ans => ans.id === option.id);
                return isSelected 
                    ? prev.filter(ans => ans.id !== option.id)
                    : [...prev, option];
            });
        } else {
            setIsAnimating(true);
            setSelectedAnswers([option]);
            
            if (!isInSlider) {
                setShowResults(true);
            }
            
            setLocalClickPosition({ x: event.clientX, y: event.clientY });
            setLocalShowConfetti(true);
            if (setClickPosition) setClickPosition({ x: event.clientX, y: event.clientY });
            if (setShowConfetti) setShowConfetti(true);
            
            setTimeout(() => {
                setLocalShowConfetti(false);
                setIsAnimating(false);
                onAnswer(option, event);
            }, isInSlider ? 500 : 2000);
        }
    };

    const handleContinue = (event) => {
        if (isTransitioning || isAnimating) return;
        
        setIsAnimating(true);
        setShowResults(true);
        if (setClickPosition) {
            setClickPosition({ x: event.clientX, y: event.clientY });
        }
        
        const allEmojis = selectedAnswers.flatMap(answer => answer.emojis);
        const randomEmojis = [...allEmojis]
            .sort(() => Math.random() - 0.5)
            .slice(0, 5);
            
        setActiveEmojis(randomEmojis);
        setLocalShowConfetti(true);
        if (setShowConfetti) {
            setShowConfetti(true);
        }

        setTimeout(() => {
            setLocalShowConfetti(false);
            if (setShowConfetti) {
                setShowConfetti(false);
            }
            setIsAnimating(false);
            onAnswer(selectedAnswers);
        }, 2000);
    };

    return (
        <div 
            className={classNames(
                "w-full",
                { "pointer-events-none": isTransitioning || isAnimating }
            )}
        >
            <Confetti 
                isActive={showConfetti}
                clickPosition={clickPosition}
                emojis={selectedAnswers.length === 1 ? selectedAnswers[0]?.emojis : activeEmojis}
            />
            <div className={classNames(
                "w-full pb-6",
                { 
                    "pointer-events-none": isTransitioning || isAnimating,
                    "transition-all duration-500 ease-in-out": isTransitioning,
                    "translate-x-full opacity-0": isTransitioning && direction === 'back',
                    "-translate-x-full opacity-0": isTransitioning && direction === 'forward',
                }
            )}>
                <div className="px-5 pt-6 pb-1">
                    <h2 
                        className={classNames(
                            "text-lg text-gray-700 font-semibold text-center mb-3",
                            "transition-all duration-500 ease-out",
                            {
                                "opacity-0 translate-y-4": !showContent,
                                "opacity-100 translate-y-0": showContent
                            }
                        )}
                    >
                        {data.text}
                    </h2>
                </div>
                <div className="px-5 space-y-3">
                    {data.answers.map((option, index) => (
                        <QuestionOption
                            key={option.id}
                            option={option}
                            index={index}
                            isSelected={selectedAnswers.some(ans => ans.id === option.id)}
                            onClick={(e) => handleOptionClick(option, e)}
                            showResults={showResults}
                            showContent={showContent}
                            animationDelay={index * 100}
                        />
                    ))}
                </div>
            </div>
            
            {data.isMultipleChoice && (
                <div className={classNames(
                    "fixed bottom-0 left-0 right-0 p-5",
                    "transition-all duration-500 ease-out",
                    {
                        "opacity-100 translate-y-0": selectedAnswers.length > 0 && showContent && !isTransitioning,
                        "opacity-0 translate-y-8 pointer-events-none": selectedAnswers.length === 0 || !showContent || isTransitioning
                    }
                )}>
                    <PorangeButton
                        full={true}
                        className="h-[60px] rounded-[20px] text-lg shadow-lg"
                        onClick={handleContinue}
                    >
                        Continue
                    </PorangeButton>
                </div>
            )}
        </div>
    );
};

const QuestionOption = ({ 
    option, 
    isSelected, 
    showResults,
    onClick,
    showContent,
    animationDelay
}) => {
    const responseCount = Math.round(BaseResponseCount * option.ratio);
    
    return (
        <button 
            onClick={(e) => onClick(e)}
            style={{ 
                transitionDelay: `${animationDelay}ms`
            }}
            className={classNames(
                "w-full p-4 text-left rounded-xl mb-3 border",
                "transition-all duration-500 ease-out",
                "bg-white hover:bg-gray-50",
                "flex items-center justify-between",
                "relative overflow-hidden",
                {
                    "opacity-0 translate-y-4": !showContent,
                    "opacity-100 translate-y-0": showContent,
                    "border-pink-500": isSelected,
                    "border-gray-300": !isSelected
                }
            )}
        >
            <span className="z-20 relative font-normal text-black">
                {option.text}
            </span>
            
            <div 
                className="absolute inset-0 bg-pink-50 transition-transform duration-[1500ms]"
                style={{ 
                    transformOrigin: 'left',
                    transform: `scaleX(${showResults ? option.ratio : 0})`,
                    transitionTimingFunction: 'cubic-bezier(0.05, 0.8, 0.1, 1)'
                }}
            />
            
            <div className="flex items-center gap-2 z-20 relative">
                <SocialProofAvatars
                    count={3}
                    avatarsBorderColor="white"
                />
                <div 
                    className={classNames(
                        "relative transition-all duration-500",
                        {
                            "opacity-50 blur-sm": !showResults,
                            "opacity-100 blur-none": showResults
                        }
                    )}
                    style={{ transitionDelay: '0.3s' }}
                >
                    <span className="font-normal text-black">
                        {formatCount(responseCount)}
                    </span>
                </div>
            </div>
        </button>
    );
}; 